import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import CustomButton from "../../../components/common/CustomButton";
import { useOrganizationService } from "../../../services";
import SelectEntities from "../../SelectEntity/SelectEntities";
import { ReduxStateType } from "../../../redux/store";
import { PinIcon } from "lucide-react";

interface PinSelectionModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PinSelectionModal: React.FC<PinSelectionModalProps> = ({
  isVisible,
  onClose,
  onSuccess,
}) => {
  const [selectedEntities, setSelectedEntities] = useState<any[]>([]);
  const [initialSelectedItems, setInitialSelectedItems] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fetchPinnedItems, pinWorkItems } = useOrganizationService();
  const { pinnedProjects = [] } = useSelector((state: ReduxStateType) => state.organization);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (isVisible) {
      fetchPinnedItems({dependentOnRedux: false});
      setHasInitialized(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && !hasInitialized && pinnedProjects.length > 0) {
      setSelectedEntities(pinnedProjects);
      setInitialSelectedItems(pinnedProjects);
      setHasInitialized(true);
    }
  }, [isVisible, pinnedProjects, hasInitialized]);

  if (!isVisible) return null;

  const handlePinSelected = async () => {
    if (selectedEntities.length === 0) return;
    
    setIsSubmitting(true);
    try {


      /** Those items which were there in initial payload and not there in new selected payload are unpinned items */
      const unpinnedItems = initialSelectedItems.filter(
        t=>
          selectedEntities.findIndex(selectedId=>selectedId.id == t.id)==-1
        ).map(t=>({
        type: t.type,
        id: t.id,
        pin: false
      }));

      

      const finalPinnedItems = [...unpinnedItems, ...selectedEntities.map((entity) => ({
        type: entity.type,
        id: entity.id,
        pin: true,
      }))]
      
      ;
      await pinWorkItems(finalPinnedItems);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to pin items:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div
        className="bg-white dark:bg-secondary-dark rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-light-border-default dark:border-gray-700 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 pt-4 pb-2 flex items-center gap-2">

          <b className="text-gray-600 dark:text-gray-300 text-xs uppercase font-bold">
            Select Pin Items</b>
        </div>
        <div className="p-3 pb-[30px]">
          <SelectEntities
            defaultSelectedItems={selectedEntities}
            pinnedItems={selectedEntities}
            onChange={setSelectedEntities}
            entityTypeLabel="Entity"
            enableNoRelationSelection={false}
          />
        </div>
        {/* Footer */}
        <div className="relative px-6 py-4 bg-gray-50 dark:bg-gray-800 z-20 border-t border-light-border-default dark:border-gray-700 flex justify-end gap-3">
          <CustomButton
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handlePinSelected}
            disabled={selectedEntities.length === 0 || isSubmitting}
            isLoading={isSubmitting}
          >
            Pin {selectedEntities.length}{" "}
            {selectedEntities.length === 1 ? "Item" : "Items"}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default PinSelectionModal;
